import React, { useState, useEffect } from 'react';
import { taskDetailsService } from '../features/task/services/taskDetailsService';
import { getCurrentStatus, STATUS_NAME_REPORTED, STATUS_NAME_OPEN, STATUS_WORK_COMPLETED,STATUS_NAME_WORK_REPORT_COMPLETED,STATUS_NAME_WORK_ON_HOLD,STATUS_NAME_INSPECTION_SKIPPED,STATUS_NAME_ESTIMATION_SKIPPED,STATUS_NAME_ESTIMATION_APPROVED,STATUS_REJECTED} from '../shared/constants/statusConstants';
import { parseAndConvertToLocal, twentyFourHrFormat,convertMinutesToHoursAndMinutesShort } from '../shared/utils/dateFormatter';
import './StepperComponent.css';

const StepperComponent = ({ taskId, isMobile, reportedDate, openDate }) => {
  const [stepperList, setStepperList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const baseSteps = [
    { status: 0, statusText: 'Reported' },
    { status: 0, statusText: 'Open' },
    { status: 0, statusText: 'Accepted' },
    { status: 0, statusText: 'Inspection completed' },
    { status: 0, statusText: 'Estimation completed' },
    { status: 0, statusText: 'Estimation approved' },
    { status: 0, statusText: 'Work report completed' },
    { status: 0, statusText: 'Work completed' }
  ];

  const stepMapping = {
    'Inspection skipped': 'Inspection completed',
    'Estimation skipped': 'Estimation completed'
  };



  const getStepColor = (status, statusText) => {
    // Check for rejected status first
    if (status === STATUS_REJECTED || statusText === 'Rejected') {
      return '#FF0000'; // rejected - red
    }
    
    const currentStatus = getCurrentStatus(status);
    switch (currentStatus) {
      case 6:
        return '#3498db'; // completed - blue
      case 4:
        return 'rgba(52, 152, 219, 0.5)'; // skipped - light blue
      case 1:
        return 'rgba(52, 152, 219, 0.8)'; // in progress - medium blue
      case 5:
        return '#FF0000'; // rejected - red
      default:
        return '#bdc3c7'; // not attended - gray
    }
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ')[0];
  };

  const getFormattedTime = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ').slice(1).join(' ');
  };

  const processStepperData = (receivedData) => {
    const uniqueMap = {};
    
    receivedData.forEach(item => {
      const key = item.status_text;
      if (!uniqueMap[key] || new Date(item.updated_at || item.created_at) > new Date(uniqueMap[key].updated_at || uniqueMap[key].created_at)) {
        uniqueMap[key] = item;
      }
    });

    const uniqueData = Object.values(uniqueMap).sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );

    // Build dynamic steps based on API response
    let steps = [...baseSteps];
    
    // Check for rejected status and replace Accepted step
    const rejectedItem = uniqueData.find(item => item.status === STATUS_REJECTED);
    if (rejectedItem) {
      const acceptedIndex = steps.findIndex(step => step.statusText === 'Accepted');
      if (acceptedIndex !== -1) {
        steps[acceptedIndex] = { 
          status: STATUS_REJECTED, 
          statusText: 'Rejected',
          createdAt: rejectedItem.created_at,
          updatedAt: rejectedItem.updated_at
        };
      }
    }
    
    // Check for skipped steps and estimation approval
    const hasSkippedInspection = uniqueData.some(item => item.status_text === STATUS_NAME_INSPECTION_SKIPPED);
    const hasSkippedEstimation = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_SKIPPED);
    const hasEstimationApproval = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_APPROVED);
    
    // Remove estimation approval if not present in API or if estimation is skipped
    if (!hasEstimationApproval || hasSkippedEstimation) {
      steps = steps.filter(step => step.statusText !== STATUS_NAME_ESTIMATION_APPROVED);
    }
    
    // Check for Hold status - only show if it's the latest status in original data
    const latestOriginalItem = receivedData[receivedData.length - 1];
    const showHold = latestOriginalItem && latestOriginalItem.status_text === STATUS_NAME_WORK_ON_HOLD;
    
    if (showHold) {
      const workReportIndex = steps.findIndex(step => step.statusText === STATUS_NAME_WORK_REPORT_COMPLETED);
      if (workReportIndex !== -1) {
        steps.splice(workReportIndex, 0, { status: 0, statusText: STATUS_NAME_WORK_ON_HOLD });
      }
    }
    
    // Update steps with API data
    uniqueData.forEach(apiItem => {
      const mappedStep = stepMapping[apiItem.status_text] || apiItem.status_text;
      const stepIndex = steps.findIndex(step => step.statusText === mappedStep);
      
      if (stepIndex !== -1) {
        steps[stepIndex] = {
          status: apiItem.status,
          statusText: apiItem.status_text,
          createdAt: apiItem.created_at,
          updatedAt: apiItem.updated_at
        };
      }
    });
    
    // Set dates for Reported and Open steps
    const reportedIndex = steps.findIndex(step => step.statusText === STATUS_NAME_REPORTED);
    if (reportedIndex !== -1 && reportedDate) {
      steps[reportedIndex].createdAt = reportedDate;
      steps[reportedIndex].status = STATUS_WORK_COMPLETED;
    }
    
    const openIndex = steps.findIndex(step => step.statusText === STATUS_NAME_OPEN);
    if (openIndex !== -1 && (reportedDate || openDate)) {
      steps[openIndex].createdAt = reportedDate || openDate;
      steps[openIndex].status = STATUS_WORK_COMPLETED;
    }
    
    return steps;
  };

  useEffect(() => {
    const fetchSteps = async () => {
      if (!taskId) return;

      try {
        setIsLoading(true);
        const response = await taskDetailsService.getMaintenanceRequestDetails(taskId, 'task_history');
        
        if (response.success && response.data) {
          const processedSteps = processStepperData(response.data);
          setStepperList(processedSteps);
        }
      } catch (error) {
        setErrorMessage(error.toString());
      } finally {
        setIsLoading(false);
      }
    };

    fetchSteps();
  }, [taskId, reportedDate, openDate]);

  if (isLoading) {
    return (
      <div className="stepper-loading">
        <div className="stepper-spinner"></div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="stepper-error">
        Error: {errorMessage}
      </div>
    );
  }

  return (
    <div className={`stepper-container ${isMobile ? 'mobile' : 'desktop'}`}>
      <div className="stepper-scroll">
        {stepperList.map((step, index) => (
          <div key={index} className={`step-item ${isMobile ? 'mobile' : 'desktop'}`}>
            {index !== 0 && (
              <div 
                className={`step-connector ${isMobile ? 'mobile' : 'desktop'}`}
                style={{ backgroundColor: getStepColor(step.status, step.statusText) }}
              />
            )}
            
            <div className="step-content">
              <div className={`step-text ${isMobile ? 'mobile' : 'desktop'}`}>
                {step.status === 3 ? 'Rejected By Technician' : step.statusText}
              </div>
              
              <div 
                className="step-circle"
                style={{ backgroundColor: getStepColor(step.status, step.statusText) }}
              >
                {index + 1}
              </div>
              
              <div className="step-date">
                <div>{getFormattedDate(step.createdAt)}</div>
                <div>{getFormattedTime(step.createdAt)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepperComponent;